<template>
  <div class="gam-switch">
    <label
      :class="['gam-switch-container', { active: isChecked, disabled: disabled, loading: loading }, position, type]"
      :aria-busy="loading || undefined"
    >
      <input
        v-if="type !== GamSwitchValueType.CHECKED"
        v-bind="$attrs"
        :value="value"
        :name="name"
        :type="getInputType"
        :checked="isChecked"
        :disabled="disabled || loading"
        @change="updateCheckStatus"
      />

      <span class="checkmark">
        <gam-icon
          v-if="type === GamSwitchValueType.CHECKBOX || type === GamSwitchValueType.CHECKED"
          class="checkmark-icon"
          :name="GamIconName.CHECK"
          :size="getCheckIconSize()"
        />
      </span>

      <span v-if="label || $slots.default" class="gam-switch-content">
        <span v-if="$slots.default" class="label-text"><slot /></span>

        <span v-else-if="label" class="label-text">{{ localize(label) }}</span>

        <span v-if="$slots.support" class="gam-switch-support-text"><slot /></span>

        <span v-else-if="supportText" class="gam-switch-support-text">
          {{ localize(supportText) }}
        </span>
      </span>
    </label>
  </div>
</template>
<script setup lang="ts">
import { localize } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamSwitchValueType } from '@/views/composables/constants/components/gamSwitch.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import type { GamSwitchType } from '@/views/composables/models/components/GamSwitch';
import { computed, ref, watch } from 'vue';

const props = defineProps<GamSwitchType>();

const emit = defineEmits([
  GamComponentsEmits.UPDATE_VALUE,
  GamComponentsEmits.UPDATE_CHECKED,
  GamComponentsEmits.UPDATE_CHECKBOX,
]);

const checked = ref<boolean>(props.checked);
const checkValues = ref<string[]>();

const updateCheckStatus = (value: Event) => {
  if (props.disabled || props.loading) return;
  const target = value?.target as HTMLInputElement;

  if (props.type === GamSwitchValueType.RADIO) {
    emit(GamComponentsEmits.UPDATE_VALUE, target?.value);
  } else if (props.type === GamSwitchValueType.CHECKBOX) {
    checked.value = target?.checked;
    emit(GamComponentsEmits.UPDATE_CHECKBOX, {
      value: target?.value,
      checked: target?.checked,
    });
    emit(GamComponentsEmits.UPDATE_CHECKED, checked.value);
  } else {
    checked.value = target?.checked;
    emit(GamComponentsEmits.UPDATE_CHECKED, checked.value);
  }
};

const getCheckIconSize = (): GamIconSize => {
  if (props.type === GamSwitchValueType.CHECKED) {
    return GamIconSize.X_LARGE;
  } else {
    return GamIconSize.NORMAL;
  }
};

const getInputType = computed((): string => {
  if (props.type === GamSwitchValueType.RADIO) {
    return GamSwitchValueType.RADIO;
  } else {
    return GamSwitchValueType.CHECKBOX;
  }
});

const isChecked = computed((): boolean => {
  switch (props.type) {
    case GamSwitchValueType.RADIO:
      return props.modelValue === props.value;
    case GamSwitchValueType.CHECKBOX:
    case GamSwitchValueType.SWITCH:
      return checked.value;
    default:
      return true;
  }
});

watch(
  () => props.checked,
  (value?: boolean) => {
    checked.value = value || false;
  },
);

defineExpose({
  checked,
  checkValues,
});
</script>
<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

$switch-size: 16px;
$radio-size: 8px;

.gam-switch {
  width: 100%;
  display: flex;
  overflow: hidden;
  -webkit-user-select: none;
  user-select: none;
  position: relative;
}

.gam-switch-container {
  display: flex;
  align-items: flex-start;
  overflow: hidden;
  gap: var(--spacing-md);
  cursor: pointer;
  align-self: stretch;

  &.disabled {
    pointer-events: none;
    filter: saturate(0);
    opacity: 0.5;
  }

  &.loading {
    pointer-events: none;

    .checkmark {
      background: var(--color-linear-gradient-skeleton);
      background-size: 200% 200%;
      animation: moving-bg 2s linear infinite;
    }
  }

  input {
    padding: 0;
    margin: 0;
    border: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
    z-index: 2;
    pointer-events: all;
    cursor: pointer;

    &:checked ~ .checkmark {
      &:before {
        opacity: 1 !important;
      }

      &:after {
        opacity: 1 !important;
      }

      @extend %gam-special-border;

      &:before {
        opacity: 1;
        padding: 1px;
        background: var(--color-linear-gradient);
      }
    }
  }

  .checkmark {
    display: flex;
    align-items: center;
    position: relative;
    flex-shrink: 0;
    border-radius: inherit;
    transition: opacity var(--transition);

    @extend %gam-special-border;

    &:before {
      opacity: 1;
      padding: 1px;
      background: var(--color-white-30);
    }

    @include hover {
      &:before {
        background: var(--color-white-70);
      }

      &:after {
        background: var(--color-white-100) !important;
      }
    }
  }

  &.switch {
    input {
      &:checked ~ .checkmark {
        &:after {
          background-color: var(--color-white-100);
          transform: translateX(18px);
        }
      }
    }

    .checkmark {
      width: 42px;
      height: 24px;
      border-radius: 14px;

      &:after {
        position: absolute;
        content: '';
        height: $switch-size;
        width: $switch-size;
        border-radius: 50%;
        left: 4px;
        top: 4px;
        background-color: var(--color-white-30);
        transition: all var(--transition);
      }
    }
  }

  &.checkbox {
    input {
      &:checked ~ .checkmark {
        .checkmark-icon {
          opacity: 1;
        }
      }
    }

    .checkmark {
      width: 24px;
      height: 24px;
      border-radius: 4px;

      .checkmark-icon {
        position: absolute;
        left: 4px;
        top: 4px;
        opacity: 0;
        transition: opacity var(--transition);
      }
    }
  }

  &.radio {
    .checkmark {
      width: 24px;
      height: 24px;
      border-radius: 50%;

      &:after {
        position: absolute;
        content: '';
        height: $radio-size;
        width: $radio-size;
        border-radius: 50%;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        background-color: var(--color-white-100);
        transition: opacity var(--transition);
        opacity: 0;
      }
    }
  }

  &.checked {
    .checkmark {
      padding: 0;

      &:before {
        display: none;
      }
    }
  }

  &.active {
    .gam-switch-content {
      .label-text {
        color: var(--color-white-100);
      }
    }
  }

  &.right {
    flex-direction: row-reverse;
    width: 100%;
    justify-content: space-between;
  }
}

.gam-switch-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  overflow: hidden;
  gap: 0.33rem;
  min-width: 0;
  width: 100%;
  position: relative;
}

.gam-switch-content .label-text {
  max-width: inherit;
  padding: 4px 0;
  font-size: 16px;
  font-weight: 300;
  color: var(--color-white-50);
  transition: color var(--transition);
  width: 100%;
}

.gam-switch-support-text {
  font-size: 14px;
  font-weight: 400;
  color: var(--color-white-30);
  line-height: 1.4;
  padding-bottom: 0.25rem;
  text-wrap: balance;
}
</style>
